import { useTranslation } from 'react-i18next'

/* ─── Shared table pager (MS-699) ───

   One toolbar for every paginated table in Studio. Prototype: `.table-pager`
   (agent-studio.html:2312-2354) plus `renderInvPagination` / `pagerButtonsHTML`.

   Layout: "Showing X–Y of N <noun>" on the left; Reset sort, the windowed page
   buttons, the "per page" label and the size switch on the right.

   Two details are load-bearing rather than cosmetic:

   * **Windowing.** An append-only log can run to thousands of pages, so the
     control shows « First · … · a three-page window · … · Last » instead of one
     button per page. First and Last stay present and go inert at the boundaries;
     hiding them would move every other control sideways as you page.
   * **One control height.** Left to their own padding and font metrics the page
     buttons come out ~24px against the segmented switch's 28px, and the
     difference reads as misalignment on a single row. The height is set once, in
     `.table-pager` (index.css), for every control on the row.

   There is deliberately no *Next* button: it duplicates the window's right-hand
   page and makes the row's meaning depend on which control you happen to use. */

export interface PaginationCountInfo {
  from: number
  to: number
  total: number
}

export interface PaginationProps {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  /** Rows actually rendered on this page — the last page is usually short. */
  itemsOnPage: number
  onPageChange: (page: number) => void
  /**
   * Renders the counter. A callback rather than a `noun` prop because "Showing
   * 21–30 of 95 invocations" has to be one translatable sentence per surface —
   * interpolating a noun into a shared sentence produces text no translator can
   * make grammatical.
   */
  renderCount: (info: PaginationCountInfo) => string
  /** Shown instead of the range when there is nothing to page through. */
  emptyLabel: string
  /** The sizes offered. Ignored unless `onPageSizeChange` is given. */
  pageSizeOptions?: number[]
  /** Omit to hide the size switch entirely (a table with a fixed page size). */
  onPageSizeChange?: (size: number) => void
  /** Omit to hide Reset sort (a table with no sort controls). */
  onResetSort?: () => void
  isSortDefault?: boolean
}

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

/**
 * `[start, start+1, start+2]` clamped into range, or every page when there are
 * three or fewer. Mirrors the prototype's `pagerButtonsHTML` exactly: a fixed
 * three-page window rather than a "current ± 1" window, so the control's width
 * never changes as you page and the buttons stay where your cursor left them.
 */
export function buildPageWindow(page: number, totalPages: number): number[] {
  if (totalPages <= 0) return []
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1)
  const preferred = page <= 1 ? 1 : page >= totalPages ? totalPages - 2 : page - 1
  const start = Math.max(1, Math.min(preferred, totalPages - 2))
  return [start, start + 1, start + 2]
}

export function Pagination({
  page,
  pageSize,
  totalCount,
  totalPages,
  itemsOnPage,
  onPageChange,
  renderCount,
  emptyLabel,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  onResetSort,
  isSortDefault = true,
}: PaginationProps) {
  const { t } = useTranslation()
  const from = (page - 1) * pageSize + 1
  const window = buildPageWindow(page, totalPages)
  const hasPages = totalCount > 0 && totalPages > 0

  return (
    <div className="table-pager">
      <span className="pager-showing">
        {totalCount > 0
          ? renderCount({ from, to: from + itemsOnPage - 1, total: totalCount })
          : emptyLabel}
      </span>

      <div className="pager-right">
        {onResetSort && (
          <button
            type="button"
            className="btn-reset-sort"
            disabled={isSortDefault}
            aria-disabled={isSortDefault}
            title={t('pagination.resetSortTitle')}
            onClick={onResetSort}
          >
            {t('pagination.resetSort')}
          </button>
        )}

        {hasPages && (
          <nav className="audit-pagination-btns" aria-label={t('pagination.ariaLabel')}>
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPageChange(1)}
            >
              {t('pagination.first')}
            </button>
            {window[0] > 1 && (
              <span className="pager-ellipsis" aria-hidden="true">
                {t('pagination.ellipsis')}
              </span>
            )}
            {window.map(n => (
              <button
                key={n}
                type="button"
                className={n === page ? 'active' : undefined}
                aria-current={n === page ? 'page' : undefined}
                aria-label={t('pagination.goToPage', { page: n })}
                onClick={() => onPageChange(n)}
              >
                {n}
              </button>
            ))}
            {window[window.length - 1] < totalPages && (
              <span className="pager-ellipsis" aria-hidden="true">
                {t('pagination.ellipsis')}
              </span>
            )}
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => onPageChange(totalPages)}
            >
              {t('pagination.last')}
            </button>
          </nav>
        )}

        {onPageSizeChange && (
          <>
            <span className="pager-perpage-label">{t('pagination.perPage')}</span>
            <div className="ops-tree-seg" role="group" aria-label={t('pagination.pageSizeAria')}>
              {pageSizeOptions.map(size => (
                <button
                  key={size}
                  type="button"
                  className={size === pageSize ? 'on' : undefined}
                  aria-pressed={size === pageSize}
                  onClick={() => onPageSizeChange(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
